/**
 * 工單轉倉「選庫」Dialog：來源倉選項與列篩選（本地，非 API）。
 */

import { buildExcludeWarehouseIdSet } from '#/modules/momMgmt/workOrder/transfer/helper/transferConfigMode';

function formatPlaceLabel(row) {
  return [row?.warehouseLabel, row?.locationLabel].filter(Boolean).join(' / ') || '';
}

/**
 * @param {string|null|undefined} excludeWarehouseId
 * @param {string[]|null|undefined} excludeWarehouseIds
 * @returns {Set<string>}
 */
function resolveExcludeSet(excludeWarehouseId, excludeWarehouseIds) {
  return buildExcludeWarehouseIdSet(excludeWarehouseId, excludeWarehouseIds);
}

/**
 * 從可轉倉庫存列去重來源倉選項；可排除一或多個倉。
 * @param {Array<{ warehouseId?: string, warehouseLabel?: string }>} stocks
 * @param {string|null|undefined} excludeWarehouseId
 * @param {{ excludeWarehouseIds?: string[] }} [options]
 * @returns {{ value: string, label: string }[]}
 */
export function buildTransferPickerWarehouseOptions(
  stocks = [],
  excludeWarehouseId = null,
  { excludeWarehouseIds = null } = {},
) {
  const excludeSet = resolveExcludeSet(excludeWarehouseId, excludeWarehouseIds);
  const byId = new Map();

  (stocks ?? []).forEach((row) => {
    const id = row?.warehouseId != null && row.warehouseId !== ''
      ? String(row.warehouseId)
      : '';
    if (!id) {
      return;
    }
    if (excludeSet.has(id)) {
      return;
    }
    if (byId.has(id)) {
      return;
    }
    byId.set(id, {
      value: id,
      label: (row.warehouseLabel && String(row.warehouseLabel).trim()) || id,
    });
  });

  return Array.from(byId.values()).sort((a, b) => (
    String(a.label).localeCompare(String(b.label), 'zh-Hant', { numeric: true })
  ));
}

/**
 * keyword ∩ 來源倉多選（空＝全部）；可排除一或多個倉列。
 * @param {object[]} stocks
 * @param {{
 *   keyword?: string,
 *   selectedWarehouseIds?: string[],
 *   excludeWarehouseId?: string|null,
 *   excludeWarehouseIds?: string[]|null,
 *   material?: { mtrNo?: string, mtrName?: string }|null,
 * }} [options]
 */
export function filterTransferPickerCandidates(stocks = [], {
  keyword = '',
  selectedWarehouseIds = [],
  excludeWarehouseId = null,
  excludeWarehouseIds = null,
  material = null,
} = {}) {
  const excludeSet = resolveExcludeSet(excludeWarehouseId, excludeWarehouseIds);
  const selected = (selectedWarehouseIds ?? [])
    .map((id) => String(id).trim())
    .filter(Boolean);
  const selectedSet = selected.length > 0 ? new Set(selected) : null;
  const q = String(keyword ?? '').trim().toLowerCase();

  const filtered = (stocks ?? []).filter((row) => {
    const warehouseId = row?.warehouseId != null && row.warehouseId !== ''
      ? String(row.warehouseId)
      : '';
    if (excludeSet.size > 0 && excludeSet.has(warehouseId)) {
      return false;
    }
    if (selectedSet && !selectedSet.has(warehouseId)) {
      return false;
    }
    if (!q) {
      return true;
    }
    const stockNo = row.stockNo ?? '';
    const place = formatPlaceLabel(row);
    const mtrHit = [material?.mtrNo, material?.mtrName]
      .some((value) => String(value ?? '').toLowerCase().includes(q));
    return mtrHit
      || [String(stockNo), String(place)].join(' ').toLowerCase().includes(q);
  });

  return [...filtered].sort((a, b) => (
    String(a?.stockNo ?? '').localeCompare(
      String(b?.stockNo ?? ''),
      undefined,
      { numeric: true, sensitivity: 'base' },
    )
  ));
}
